import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Hammer, Flame, ShieldCheck, Clock, Award } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const HERO_IMG =
    "https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const teaserIcons = [Wrench, Hammer, Flame];
const whyIcons = [ShieldCheck, Clock, Award];

export default function Home() {
    useDocumentMeta("home", "/");
    const { t } = useI18n();
    const h = t.home;

    return (
        <div data-testid="home-page" className="bg-white">
            <section className="border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        <div className="brutal-fade-up">
                            <div className="overline mb-6">{h.heroOverline}</div>
                            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.85] text-black">
                                {h.heroTitle1}
                                <br />
                                {h.heroTitle2}
                                <br />
                                <span className="text-[#FF5A00]">{h.heroTitle3}</span>
                            </h1>
                            <p className="mt-8 text-lg md:text-xl font-medium text-zinc-700 max-w-xl leading-relaxed">
                                {h.heroDesc}
                            </p>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link to="/contact" data-testid="hero-quote-btn" className="brutal-btn">
                                    {h.heroQuoteBtn} <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/services" data-testid="hero-services-btn" className="brutal-btn-outline">
                                    {h.heroServicesBtn}
                                </Link>
                            </div>
                        </div>

                        <div className="relative brutal-fade-up">
                            <div className="absolute -top-3 -left-3 w-full h-full bg-[#FF5A00]" />
                            <img
                                src={HERO_IMG}
                                alt="Plumber installing steel pipes"
                                className="relative w-full h-[420px] md:h-[520px] object-cover border-2 border-black grayscale"
                                data-testid="hero-image"
                            />
                            <div className="absolute -bottom-6 -right-3 bg-black text-white px-6 py-4 border-2 border-black hidden sm:block">
                                <div className="font-display text-4xl leading-none text-[#FF5A00]">
                                    {h.heroBadgeNumber}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.3em] font-bold mt-1">
                                    {h.heroBadgeLabel}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-black text-white overflow-hidden border-b-2 border-black">
                <div className="flex whitespace-nowrap brutal-marquee-track py-6">
                    {[...h.trustItems, ...h.trustItems, ...h.trustItems].map((tt, i) => (
                        <div
                            key={i}
                            className="flex items-center gap-8 px-8 text-sm font-bold uppercase tracking-[0.3em]"
                        >
                            <span>{tt}</span>
                            <span className="text-[#FF5A00]">◆</span>
                        </div>
                    ))}
                </div>
            </section>

            <section data-testid="services-teaser" className="border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <div className="overline mb-4">{h.servicesOverline}</div>
                            <h2 className="font-display text-5xl md:text-6xl uppercase leading-none text-black max-w-2xl">
                                {h.servicesTitle1}
                                <br />
                                {h.servicesTitle2}
                            </h2>
                        </div>
                        <Link to="/services" data-testid="all-services-link" className="brutal-btn-outline self-start md:self-auto">
                            {h.allServices} <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                        {h.services.map((s, idx) => {
                            const Icon = teaserIcons[idx];
                            return (
                                <div key={s.title} data-testid={`home-service-${idx}`} className="brutal-card p-8">
                                    <div className="flex items-start justify-between mb-8">
                                        <div className="w-14 h-14 bg-black flex items-center justify-center">
                                            <Icon className="w-7 h-7 text-[#FF5A00]" strokeWidth={2.5} />
                                        </div>
                                        <div className="font-display text-3xl text-zinc-300">0{idx + 1}</div>
                                    </div>
                                    <h3 className="font-display text-3xl uppercase leading-none text-black">{s.title}</h3>
                                    <p className="mt-4 text-zinc-700 font-medium leading-relaxed">{s.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="border-b-2 border-black bg-zinc-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="overline mb-4">{h.whyOverline}</div>
                    <h2 className="font-display text-5xl md:text-6xl uppercase leading-none text-black max-w-3xl">
                        {h.whyTitle1}
                        <br />
                        {h.whyTitle2}
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-3 mt-16 border-2 border-black bg-white">
                        {h.why.map((item, idx) => {
                            const Icon = whyIcons[idx];
                            return (
                                <div
                                    key={item.title}
                                    className={`p-8 md:p-10 ${idx !== 2 ? "md:border-r-2 border-black" : ""} border-b-2 md:border-b-0 last:border-b-0`}
                                    data-testid={`why-${idx}`}
                                >
                                    <Icon className="w-9 h-9 text-[#FF5A00]" strokeWidth={2.5} />
                                    <h3 className="font-display text-2xl uppercase mt-6 text-black">{item.title}</h3>
                                    <p className="mt-3 text-zinc-700 font-medium">{item.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="bg-[#FF5A00] border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 items-end">
                        <div className="lg:col-span-2">
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-black mb-5">
                                {h.ctaOverline}
                            </div>
                            <h2 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.85] text-black">
                                {h.ctaTitle1}
                                <br />
                                {h.ctaTitle2}
                            </h2>
                        </div>
                        <div className="flex flex-col gap-4">
                            <a href="tel:+15818889419" data-testid="cta-call-btn" className="brutal-btn justify-center text-base">
                                {h.ctaCall}
                            </a>
                            <Link to="/contact" data-testid="cta-contact-btn" className="brutal-btn-outline justify-center text-base bg-white">
                                {h.ctaQuote} <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
